import { Column, Index, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import type { TradeShowPlanItemKind } from '../../constants/trade-shows';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * One thing in a tenant's own plan for a show, shown on top of the organizer's
 * agenda (PD-17633): travel, setup, a booth shift, a side event, or going to an
 * agenda item. Booth shifts here are what booth meetings are offered around.
 */
@Entity('trade_show_plan_item')
@Index('idx_trade_show_plan_item_show', ['tradeShowId', 'itemDate'])
export class TradeShowPlanItemEntity extends BaseEntity {
	@Column('int', { name: 'fk_trade_show__id' })
	public tradeShowId!: number;

	@Column('varchar', { name: 'kind', length: 20 })
	public kind!: TradeShowPlanItemKind;

	@Column('varchar', { name: 'title', length: 255 })
	public title!: string;

	@Column('date', { name: 'itemDate' })
	public itemDate!: string;

	/** The show's local time, with no time zone: an agenda reads that way. */
	@Column('time', { name: 'startTime', nullable: true })
	public startTime?: string | null;

	@Column('time', { name: 'endTime', nullable: true })
	public endTime?: string | null;

	@Column('varchar', { name: 'place', length: 255, nullable: true })
	public place?: string | null;

	@Column('text', { name: 'notes', nullable: true })
	public notes?: string | null;

	/**
	 * Set when this is going to an agenda item: a hash of its date and title. The
	 * directory job rewrites agenda rows, so their ids don't last; this does, and
	 * the times are read live from the agenda.
	 */
	@Column('char', { name: 'agendaKey', length: 40, nullable: true })
	public agendaKey?: string | null;

	@OneToMany(() => TradeShowPlanPersonEntity, (person) => person.planItem)
	public people?: Array<TradeShowPlanPersonEntity>;
}

/** A person on a plan item, and the calendar event made for them, if any. */
@Entity('trade_show_plan_person')
@Index('idx_trade_show_plan_person_item', ['planItemId'])
@Index('idx_trade_show_plan_person_user', ['userId'])
export class TradeShowPlanPersonEntity extends BaseEntity {
	// Unsigned to match _id: a join column declared by hand does not inherit it from the relation, and MySQL refuses the foreign key otherwise.
	@Column('int', { name: 'fk_trade_show_plan_item__id', unsigned: true })
	public planItemId!: number;

	@ManyToOne(() => TradeShowPlanItemEntity, (item) => item.people)
	@JoinColumn({ name: 'fk_trade_show_plan_item__id' })
	public planItem?: TradeShowPlanItemEntity;

	@Column('int', { name: 'fk_user__id' })
	public userId!: number;

	@Column('int', { name: 'fk__event__id', nullable: true })
	public eventId?: number | null;

	/** What the calendar event was last written from, to tell when it needs updating. */
	@Column('varchar', { name: 'calendarStamp', length: 500, nullable: true })
	public calendarStamp?: string | null;
}
